import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

const TOKEN_KEY = 'authToken';
const ROLE_KEY = 'authRole';

function setHeaderToken(token) {
  if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete api.defaults.headers.common['Authorization'];
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // profissional | cliente | null
  const [role, setRole] = useState(null);     // 'profissional' | 'cliente' | null
  const [loading, setLoading] = useState(true);

  const initialize = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const savedRole = localStorage.getItem(ROLE_KEY);
    if (!token || !savedRole) { setLoading(false); return; }
    setHeaderToken(token);
    try {
      const res = await api.get(`/auth/${savedRole}/me`);
      setUser(res.data);
      setRole(savedRole);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ROLE_KEY);
      setHeaderToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { initialize(); }, [initialize]);

  const persistAndSet = (token, r, u) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(ROLE_KEY, r);
    setHeaderToken(token);
    setUser(u);
    setRole(r);
  };

  const loginProfissional = async (email, senha) => {
    const res = await api.post('/auth/profissional/login', { email, senha });
    persistAndSet(res.data.token, 'profissional', res.data.profissional);
    return res.data.profissional;
  };

  const registerProfissional = async (form) => {
    const res = await api.post('/auth/profissional/register', form);
    persistAndSet(res.data.token, 'profissional', res.data.profissional);
    return res.data.profissional;
  };

  const loginCliente = async (email, senha) => {
    const res = await api.post('/auth/cliente/login', { email, senha });
    persistAndSet(res.data.token, 'cliente', res.data.cliente);
    return res.data.cliente;
  };

  const registerCliente = async (form) => {
    const res = await api.post('/auth/cliente/register', form);
    persistAndSet(res.data.token, 'cliente', res.data.cliente);
    return res.data.cliente;
  };

  const loginAnunciante = async (email, senha) => {
    const res = await api.post('/auth/anunciante/login', { email, senha });
    persistAndSet(res.data.token, 'anunciante', res.data.anunciante);
    return res.data.anunciante;
  };

  const registerAnunciante = async (form) => {
    const res = await api.post('/auth/anunciante/register', form);
    persistAndSet(res.data.token, 'anunciante', res.data.anunciante);
    return res.data.anunciante;
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ROLE_KEY);
    setHeaderToken(null);
    setUser(null);
    setRole(null);
  };

  const updateUser = (next) => setUser(next);

  return (
    <AuthContext.Provider value={{
      user, role, loading,
      isProfissional: role === 'profissional',
      isCliente: role === 'cliente',
      isAnunciante: role === 'anunciante',
      loginProfissional, registerProfissional,
      loginCliente, registerCliente,
      loginAnunciante, registerAnunciante,
      logout, updateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
